/**
 * Seek Key — Shopify → Checkout Redirect (v2.1)
 *
 * Roda em: tema Shopify (assets/seekkey-checkout-redirect-v2.js).
 * Referenciado em: layout/theme.liquid via {{ 'seekkey-checkout-redirect-v2.js' | asset_url | script_tag }}
 *
 * Faz duas coisas:
 *   1) "Finalizar Compra" (cart drawer / cart page): captura o /cart.js,
 *      cria uma session no checkout backend (POST /api/checkout/session/init)
 *      e redireciona pra checkout/?s=<id-curto>. Fallback: ?cart=<base64>.
 *   2) "Comprar Agora" (Shopify Payment Button na página do produto):
 *      adiciona o produto ao cart e atualiza o drawer. Não redireciona —
 *      o cliente revisa o cart e clica "Finalizar Compra".
 *
 * Ambiente:
 *   - STAGING (preview_theme_id=157936419039): aponta pra staging.seekkey.com.br/checkout/
 *   - PROD (qualquer outro): aponta pra secure.seekkey.com.br/checkout/
 *   O preview_theme_id só aparece na 1ª URL — persistimos em localStorage
 *   pra navegações subsequentes.
 *
 * Tracking:
 *   - Propaga TODOS os params utm_* (não apenas os 5 padrão) + click IDs
 *     comuns (gclid, fbclid, ttclid, msclkid, wbraid, gbraid, li_fat_id,
 *     twclid, epik, irclickid). Permite criar UTMs custom (utm_id,
 *     utm_creative, utm_audience…) sem perder dado no caminho.
 *
 * Preços: Shopify entrega em CENTAVOS. Não converter aqui — checkout.js
 * faz a divisão por 100.
 */

(function () {
  'use strict';

  var STAGING_THEME_ID = '157936419039';
  var STAGING_FLAG_KEY = 'sk_shopify_preview_theme_id';

  // ──────────────────────────────────────
  // Ambient detection
  // ──────────────────────────────────────
  // Ordem de checagem (mais confiável → menos confiável):
  //   1. window.SK_THEME — injetado pelo theme.liquid via Liquid:
  //        <script>window.SK_THEME = { id: "{{ theme.id }}", role: "{{ theme.role }}", name: "{{ theme.name | escape }}" };</script>
  //      Server-side, sabe exatamente qual tema está sendo servido (preview
  //      ou main). Funciona mesmo quando ?preview_theme_id sumiu da URL e
  //      o Shopify mantém o preview via cookie.
  //   2. ?preview_theme_id na URL — só funciona na 1ª URL do preview.
  //   3. localStorage (espelho do #2) — pra navegações subsequentes na
  //      mesma sessão de preview.
  function detectStaging() {
    try {
      var t = window.SK_THEME;
      if (t && typeof t === 'object') {
        if (String(t.id) === STAGING_THEME_ID) return true;
        if (typeof t.name === 'string' && /staging/i.test(t.name)) return true;
        // role 'main' = produção; qualquer outro role + id != staging não
        // é o nosso staging conhecido (pode ser dev/demo de outra pessoa).
      }
    } catch (e) {}
    try {
      var params = new URLSearchParams(window.location.search);
      var previewId = params.get('preview_theme_id');
      if (previewId === STAGING_THEME_ID) {
        try { localStorage.setItem(STAGING_FLAG_KEY, previewId); } catch (e) {}
        return true;
      }
      try {
        if (localStorage.getItem(STAGING_FLAG_KEY) === STAGING_THEME_ID) return true;
      } catch (e) {}
    } catch (e) {}
    return false;
  }

  var IS_STAGING       = detectStaging();
  // Em PROD, checkout vive em secure.seekkey.com.br/checkout/ (mesmo subdomínio
  // do portal/conta/admin). NUNCA usar checkout.seekkey.com.br/ — esse subdomínio
  // é legacy e quebra cookies cross-subdomain do Meta Pixel (_fbp). Incidente
  // mai/2026: cobertura Purchase caiu 98% → 36% quando este arquivo apontou
  // erroneamente pra checkout. — ver /opt/seekkey-staging/checkout/current/DEPLOY-BRIEFING-TRACKING.md
  var BASE_HOST        = IS_STAGING ? 'https://staging.seekkey.com.br' : 'https://secure.seekkey.com.br';
  var CHECKOUT_URL     = BASE_HOST + '/checkout/';
  var SESSION_INIT_URL = BASE_HOST + '/api/checkout/session/init';

  // ──────────────────────────────────────
  // State
  // ──────────────────────────────────────
  var isRedirecting = false;

  // bfcache restore (back button) — reseta o flag pra permitir nova tentativa
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) isRedirecting = false;
  });

  function resetRedirectState() { isRedirecting = false; }

  // ──────────────────────────────────────
  // Tracking propagation (UTMs + click IDs)
  // ──────────────────────────────────────
  // Click IDs comuns de mídia paga. UTMs são detectadas dinamicamente
  // (qualquer param que comece com utm_) — UTMs custom funcionam.
  var CLICK_ID_KEYS = [
    'gclid', 'fbclid', 'ttclid', 'msclkid', 'wbraid', 'gbraid', 'li_fat_id',
    'twclid', 'epik', 'irclickid',
    // Q-235 B2: srsltid (orgânico Merchant Center) + gad_* (anúncio, às vezes
    // sem gclid) — só ADICIONA query param repassado; domínio/path intocados
    'srsltid', 'gad_source', 'gad_campaignid'
  ];

  function getTrackingQuery() {
    var params = new URLSearchParams(window.location.search);
    var pairs = [];
    for (var entry of params.entries()) {
      var k = entry[0];
      var v = entry[1];
      if (!v) continue;
      var lk = String(k).toLowerCase();
      if (lk.indexOf('utm_') === 0 || CLICK_ID_KEYS.indexOf(lk) !== -1) {
        pairs.push(encodeURIComponent(k) + '=' + encodeURIComponent(v));
      }
    }
    return pairs.length ? '&' + pairs.join('&') : '';
  }

  // ──────────────────────────────────────
  // Cart fetch + payload
  // ──────────────────────────────────────
  function getShopifyCart() {
    return fetch('/cart.js', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    })
    .then(function (r) {
      if (!r.ok) throw new Error('cart fetch failed: ' + r.status);
      return r.json();
    });
  }

  function buildPayload(cart) {
    if (!cart || !cart.items || cart.items.length === 0) return null;

    return {
      token: cart.token,
      item_count: cart.item_count,
      total_price: cart.total_price,
      items: cart.items.map(function (item) {
        // Defense-in-depth: força tipos numéricos. Cliente envia, backend valida
        // de novo, mas filtramos lixo aqui pra payload menor / log mais limpo.
        var qty = parseInt(item.quantity, 10);
        if (!Number.isFinite(qty) || qty < 1) qty = 1;
        if (qty > 50) qty = 50;
        var price = parseInt(item.price, 10);
        if (!Number.isFinite(price) || price < 0) price = 0;
        var linePrice = parseInt(item.line_price, 10);
        if (!Number.isFinite(linePrice) || linePrice < 0) linePrice = price * qty;

        return {
          id: item.id,
          variant_id: item.variant_id,
          product_id: item.product_id,
          title: item.title,
          product_title: item.product_title,
          variant_title: item.variant_title,
          product_type: item.product_type,
          sku: item.sku,
          quantity: qty,
          price: price,
          line_price: linePrice,
          image: item.image,
          handle: item.handle,
          vendor: item.vendor,
          // Q-375: repassa SÓ a origem do item (_sk_origin: combo_pdp/combo_drawer).
          // O checkout valida por whitelist; sem origem o campo some do JSON.
          properties: (item.properties && typeof item.properties._sk_origin === 'string')
            ? { _sk_origin: item.properties._sk_origin.slice(0, 32) }
            : undefined
        };
      })
    };
  }

  // ──────────────────────────────────────
  // Redirect strategies
  // ──────────────────────────────────────
  function redirectViaSession(payload) {
    return fetch(SESSION_INIT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    .then(function (r) {
      if (!r.ok) throw new Error('init failed: ' + r.status);
      return r.json();
    })
    .then(function (data) {
      if (!data || !data.session_id) throw new Error('no session_id');
      window.location.href = CHECKOUT_URL + '?s=' + encodeURIComponent(data.session_id) + getTrackingQuery();
    });
  }

  function redirectViaLegacyEncoded(payload) {
    var encoded = btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
    window.location.href = CHECKOUT_URL + '?cart=' + encoded + getTrackingQuery();
  }

  function redirectFromCart() {
    if (isRedirecting) return;
    isRedirecting = true;

    getShopifyCart().then(function (cart) {
      var payload = buildPayload(cart);
      if (!payload) {
        resetRedirectState();
        alert('Seu carrinho está vazio.');
        return;
      }
      return redirectViaSession(payload).catch(function (err) {
        // Fallback pro método legado garante que o cliente sempre consegue
        // chegar ao checkout — mesmo que o backend de session esteja down.
        console.warn('[seekkey] session init failed, fallback to legacy:', err);
        redirectViaLegacyEncoded(payload);
      });
    }).catch(function (err) {
      console.error('[seekkey] cart fetch failed:', err);
      resetRedirectState();
      alert('Erro ao processar. Tente novamente.');
    });
  }

  // ──────────────────────────────────────
  // "Comprar agora" (Shopify Payment Button) — adiciona ao cart e refresha drawer
  // ──────────────────────────────────────
  function buyNowAndAddToCart() {
    var form = document.querySelector('form[action*="/cart/add"]');
    if (!form) {
      resetRedirectState();
      return;
    }

    var formData = new FormData(form);
    var variantId = formData.get('id');
    var quantity = formData.get('quantity') || 1;

    if (!variantId) {
      var idInput = form.querySelector('input[name="id"], select[name="id"]');
      if (idInput) variantId = idInput.value;
    }
    if (!variantId) {
      resetRedirectState();
      return;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: parseInt(variantId, 10),
        quantity: parseInt(quantity, 10)
      })
    })
    .then(function (r) {
      if (!r.ok) throw new Error('add failed');
      return r.json();
    })
    .then(function () {
      resetRedirectState();
      // Re-render das sections de carrinho (Shopify Online Store 2.0)
      if (typeof window.Shopify !== 'undefined' && window.Shopify.theme) {
        fetch('/?sections=cart-icon-bubble,cart-drawer')
          .then(function (r) { return r.json(); })
          .then(function (sections) {
            for (var key in sections) {
              var el = document.getElementById('shopify-section-' + key);
              if (el) el.outerHTML = sections[key];
            }
          })
          .catch(function () {});
      }
      // Fallback: reload pra refletir cart atualizado
      window.location.reload();
    })
    .catch(function (err) {
      console.error('[seekkey] add to cart failed:', err);
      resetRedirectState();
    });
  }

  // ──────────────────────────────────────
  // Init: wire click/submit/fetch listeners
  // ──────────────────────────────────────
  function init() {
    // Seletores de "Finalizar Compra" — cobre o que vimos em 7+ temas Shopify
    // (Dawn, Impulse, Prestige, Symmetry, etc.) + drawer customizado da Seek Key.
    var checkoutSelectors = [
      'button[name="checkout"]',
      'input[name="checkout"]',
      '[type="submit"][name="checkout"]',
      'a[href="/checkout"]',
      'a[href*="/checkout"]',
      '.cart__checkout-button',
      '#checkout',
      '#CartDrawer-Checkout',
      '[data-action="checkout"]',
      '.cart-checkout-button'
    ];

    // Seletores de "Comprar Agora" (Shopify Payment Request Button + variantes)
    var buyNowSelectors = [
      '.shopify-payment-button button',
      '.shopify-payment-button__button',
      '.shopify-payment-button__button--unbranded',
      '.dynamic-checkout__content button',
      '[data-testid="Checkout-button"]'
    ];

    function matchesAny(target, selectors) {
      for (var i = 0; i < selectors.length; i++) {
        if (target.closest(selectors[i])) return true;
      }
      return false;
    }

    document.addEventListener('click', function (event) {
      var target = event.target;
      var isCheckout = matchesAny(target, checkoutSelectors);
      var isBuyNow  = !isCheckout && matchesAny(target, buyNowSelectors);

      if (!isCheckout && !isBuyNow) return;

      // Bloqueia comportamento nativo (do tema/Shopify) ANTES de qualquer outra coisa.
      // stopImmediatePropagation impede outros handlers do tema de rodarem em paralelo.
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (isRedirecting) return;

      if (isCheckout) {
        // Se o link já aponta pro nosso checkout, deixa seguir (idempotência:
        // evita loops em links pré-construídos com o destino correto).
        var anchor = target.closest('a[href]');
        if (anchor && anchor.href && anchor.href.indexOf(BASE_HOST) === 0) {
          window.location.href = anchor.href;
          return;
        }
        redirectFromCart();
      } else {
        isRedirecting = true;
        buyNowAndAddToCart();
      }
    }, true);

    document.addEventListener('submit', function (event) {
      var form = event.target;
      var action = form.action || '';
      var submitterName = event.submitter ? (event.submitter.name || '') : '';

      // 1) Submit em form de checkout (nome ou action)
      if (action.indexOf('/checkout') !== -1 || submitterName === 'checkout') {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        redirectFromCart();
        return;
      }

      // 2) Submit em form /cart/add com submitter de "Comprar Agora"
      if (event.submitter && action.indexOf('/cart/add') !== -1) {
        for (var i = 0; i < buyNowSelectors.length; i++) {
          if (event.submitter.closest(buyNowSelectors[i])) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            if (!isRedirecting) {
              isRedirecting = true;
              buyNowAndAddToCart();
            }
            return;
          }
        }
      }
    }, true);

    // Intercepta fetch programático pro /checkout. Alguns temas disparam
    // o redirect via JS (sem clique direto), tipo cart drawer com lazy nav.
    var originalFetch = window.fetch;
    window.fetch = function () {
      var url = arguments[0];
      if (typeof url === 'string'
          && url.indexOf('/checkout') !== -1
          && url.indexOf(BASE_HOST) === -1) {
        if (isRedirecting) return new Promise(function () {});
        isRedirecting = true;
        redirectFromCart();
        // Promise sem resolver — o caller não recebe response, mas vamos navegar
        return new Promise(function () {});
      }
      return originalFetch.apply(this, arguments);
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Debug helper — disponível no console como window.__skRedirectInfo
  try {
    window.__skRedirectInfo = {
      version: 'v2.2',
      isStaging: IS_STAGING,
      baseHost: BASE_HOST,
      checkoutUrl: CHECKOUT_URL,
      sessionInitUrl: SESSION_INIT_URL,
      themeData: window.SK_THEME || null,
      previewParam: (function () {
        try { return new URLSearchParams(window.location.search).get('preview_theme_id'); } catch (e) { return null; }
      })(),
      previewLocalStorage: (function () {
        try { return localStorage.getItem(STAGING_FLAG_KEY); } catch (e) { return null; }
      })()
    };
  } catch (e) {}

})();
